import { titleKey } from "./titleKey";
export { titleKey };
/**
 * UAE Bucket List helpers.
 *
 * Bucket List items are ordinary goals with category "Bucket List" and no
 * deadline — the existing Dreams tab, Lived-It flow and partner matching keep
 * working unchanged. Guests save to localStorage ("guestGoals"); on sign-in,
 * migrateGuestGoals() moves those items into the user's account once.
 */
import { supabase } from "@/integrations/supabase/client";
import { BUCKET_LIST_CATEGORY } from "@/constants/categories";
import type { Goal } from "@/types/todo";
import { logger } from "@/utils/logger";

const GUEST_GOALS_KEY = "guestGoals";
export const GOALS_CHANGED_EVENT = "goaldigger:goals-changed";

export function bucketTitleForPlace(name: string, area?: string | null) {
  return area ? `Visit ${name} (${area})` : `Visit ${name}`;
}

function readGuestGoals(): Goal[] {
  try {
    const raw = localStorage.getItem(GUEST_GOALS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function bucketCategoryId(): Promise<string | null> {
  const { data } = await supabase
    .from("categories")
    .select("id")
    .eq("name", BUCKET_LIST_CATEGORY)
    .maybeSingle();
  return data?.id ?? null;
}

export type AddResult = "added" | "exists" | "error";

/** Save a place/stop to the bucket list for a guest or signed-in user. */
export async function addToBucketList(title: string, userId?: string | null): Promise<AddResult> {
  const clean = title.trim();
  if (!clean) return "error";

  if (!userId) {
    const goals = readGuestGoals();
    if (goals.some((g) => g.title === clean && g.category === BUCKET_LIST_CATEGORY)) return "exists";
    goals.push({
      id: crypto.randomUUID(),
      title: clean,
      progress: 0,
      dueDate: "",
      category: BUCKET_LIST_CATEGORY,
      todos: [],
      isExample: false,
    });
    localStorage.setItem(GUEST_GOALS_KEY, JSON.stringify(goals));
    window.dispatchEvent(new Event(GOALS_CHANGED_EVENT));
    return "added";
  }

  try {
    const { data: existing } = await supabase
      .from("goals")
      .select("id")
      .eq("user_id", userId)
      .eq("title", clean)
      .limit(1);
    if (existing && existing.length > 0) return "exists";

    const { error } = await supabase.from("goals").insert([
      { title: clean, category_id: await bucketCategoryId(), due_date: null, progress: 0, user_id: userId },
    ]);
    if (error) throw error;
    window.dispatchEvent(new Event(GOALS_CHANGED_EVENT));
    return "added";
  } catch (err) {
    logger.error("[bucketList] add failed", err);
    return "error";
  }
}

/**
 * Move a guest's locally saved goals into their account after sign-in.
 * Only removes local items once the insert succeeds, so nothing is lost.
 */
export async function migrateGuestGoals(userId: string): Promise<number> {
  const guest = readGuestGoals().filter((g) => !g.isExample && g.title?.trim());
  if (guest.length === 0) return 0;

  try {
    const { data: categories } = await supabase.from("categories").select("id, name");
    const catId = (name?: string) => categories?.find((c) => c.name === name)?.id ?? null;

    const { data: existing } = await supabase.from("goals").select("title").eq("user_id", userId);
    // Dedup ignores case/extra spaces, and also skips repeats inside the guest list itself.
    const taken = new Set((existing ?? []).map((g) => titleKey(g.title)));

    const kept = guest.filter((g) => {
      const key = titleKey(g.title);
      if (taken.has(key)) return false;
      taken.add(key);
      return true;
    });

    const rows = kept.map((g) => ({
      title: g.title.trim(),
      category_id: catId(g.category),
      due_date: g.dueDate ? g.dueDate : null,
      progress: Math.max(0, Math.min(100, g.progress ?? 0)),
      completed_at: g.completedAt ?? null,
      user_id: userId,
    }));

    if (rows.length > 0) {
      // Insert goals and get their new ids back (same order as `rows`).
      const { data: inserted, error } = await supabase.from("goals").insert(rows).select("id");
      if (error) throw error;

      // Carry over each goal's steps (todos) and sub-steps (subtasks) so
      // nothing the guest planned is lost.
      for (let i = 0; i < kept.length; i++) {
        const goalId = inserted?.[i]?.id;
        const todos = kept[i].todos ?? [];
        if (!goalId || todos.length === 0) continue;

        const { data: insertedTodos, error: todoError } = await supabase
          .from("todos")
          .insert(
            todos.map((t) => ({
              goal_id: goalId,
              user_id: userId,
              text: t.text,
              due_date: t.dueDate ? t.dueDate : null,
              completed: t.completed ?? false,
            }))
          )
          .select("id");
        if (todoError) throw todoError;

        const subtaskRows = todos.flatMap((t, j) =>
          (t.subtasks ?? []).map((s) => ({
            todo_id: insertedTodos?.[j]?.id,
            text: s.text,
            due_date: s.dueDate ? s.dueDate : null,
            completed: s.completed ?? false,
          }))
        ).filter((s) => s.todo_id);

        if (subtaskRows.length > 0) {
          const { error: subError } = await supabase.from("subtasks").insert(subtaskRows);
          if (subError) throw subError;
        }
      }
    }
    localStorage.removeItem(GUEST_GOALS_KEY);
    window.dispatchEvent(new Event(GOALS_CHANGED_EVENT));
    return rows.length;
  } catch (err) {
    logger.warn("[bucketList] guest migration failed; local items kept", err);
    return 0;
  }
}
